// Layer 3 (Transformations): the Daily meeting board (polish.P12 #666, the
// locked comp `.impeccable/mocks/decision/meeting.html`, variant B). Pure
// projections over the module's existing transforms — the header figures, the
// 24 h band, the bottleneck read split into its parts, the grouped plan
// variations and the tab headlines. Nothing here restates a figure: every
// value is read from `Scene.transform.ts`, `PerfTab.transform.ts` or
// `NextDayPlan.transform.ts`. No React, no engine mutations.

import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { KpiTile } from '@/versions/v1/schema/plant-role';
import {
  cccReview,
  handoverForShift,
  type PreviousThreeWsBucket,
  last24hVsPlan,
  meetingKpis,
  openThreeWs,
  threeWPriority,
} from './Scene.transform';
import { formatDeltaPct, perfRows, perfStrip, windowsFor } from './PerfTab.transform';
import { nextDayPlanRows } from './NextDayPlan.transform';
import { CONTROL_VERIFICATION_TARGET_PCT } from '@/versions/v1/modules/risk/seed';
import { MEETING_CURSOR, THREE_W_ENTRIES, TODAY_MEETING_INSTANCE, type ControlVerificationCheck } from './seed';

/** The meeting's time box and the control-verification bar — the two
 * benchmarks the header compares against (seeded assumptions, logic doc
 * § Open questions). Moved here from the board's JSX, where they were typed. */
export const MEETING_WINDOW_MIN = 20;
/** The CV bar is Risk's (`risk/seed.ts`, comp adjudication 5); the meeting reads it. */
export { CONTROL_VERIFICATION_TARGET_PCT } from '@/versions/v1/modules/risk/seed';
export const ON_TIME_START_TARGET_PCT = 95;

export type HeaderFigure = {
  id: 'meeting' | 'on-time' | 'control-verification' | 'open' | 'ccc-today';
  label: string;
  value: string;
  /** The comparison beneath the figure: an optional prefix, a lead term in
   * 600 ink-secondary, then the rest. */
  pre: string;
  lead: string | null;
  rest: string;
  /** Only the abnormal reading takes a state colour (DESIGN.md § The primary figure). */
  tone: 'warn' | null;
};

function minutesOf(clock: string): number {
  const [h, m] = clock.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Pass / total over the shift's spot checks — the handover table's own rows,
 * so the header's "8 of 9 verified" and the Field spot-check results panel
 * agree. `label` is the one phrase both surfaces render: the pass count reads
 * "verified", not "pass", because a reader counts the "Verified — late" pill
 * (CV-07) among the passes. */
export function controlCheckTally(checks: readonly ControlVerificationCheck[]): { pass: number; total: number; pct: number; label: string } {
  const pass = checks.filter((check) => check.result === 'pass').length;
  return { pass, total: checks.length, pct: checks.length ? Math.round((pass / checks.length) * 100) : 0, label: `${pass} of ${checks.length} verified` };
}

/** The register rows whose original commit is today and that carry a CCC
 * case — the rows the header's "CCC required today" counts. */
export function cccRequiredToday(cursor: string = MEETING_CURSOR) {
  return THREE_W_ENTRIES.filter((row) => row.originalCommit === cursor && Boolean(row.cccCaseId));
}

export function meetingHeaderFigures(state: SessionState): readonly HeaderFigure[] {
  const kpis = meetingKpis();
  const meeting = TODAY_MEETING_INSTANCE;
  const used = minutesOf(meeting.endTime) - minutesOf(meeting.startTime);
  const checks = controlCheckTally(handoverForShift(state).checks);
  const cccToday = cccRequiredToday();
  return [
    {
      id: 'meeting',
      label: 'Today’s meeting',
      value: `${meeting.startTime}–${meeting.endTime}`,
      pre: meeting.onTimeStart ? 'on time · ' : '',
      lead: `${used} of ${MEETING_WINDOW_MIN} min`,
      rest: ` · ${meeting.attendees.length} attendees`,
      tone: null,
    },
    {
      id: 'on-time',
      label: 'On-time start',
      value: `${kpis.onTimeStartPct}%`,
      pre: '',
      lead: null,
      rest: 'rolling 30 days',
      tone: kpis.onTimeStartPct >= ON_TIME_START_TARGET_PCT ? null : 'warn',
    },
    {
      id: 'control-verification',
      label: 'Control verification',
      value: `${checks.pct}%`,
      pre: '',
      lead: checks.label,
      rest: ` · target ${CONTROL_VERIFICATION_TARGET_PCT}%`,
      tone: checks.pct >= CONTROL_VERIFICATION_TARGET_PCT ? null : 'warn',
    },
    {
      id: 'open',
      label: 'Open 3Ws',
      value: String(kpis.openThreeWCount),
      pre: '',
      lead: String(kpis.agedOver14dCount),
      rest: ' aged > 14 days',
      tone: null,
    },
    {
      id: 'ccc-today',
      label: 'CCC required today',
      value: String(kpis.cccRequiredTodayCount),
      pre: '',
      lead: null,
      rest: cccToday.map((row) => `${row.id} → ${row.cccCaseId}`).join(' · '),
      tone: null,
    },
  ];
}

// --- The 24 h band ----------------------------------------------------------

export type BandCell = {
  id: string;
  label: string;
  /** The figure and its unit, split so the unit sits on the baseline in muted text. */
  figure: string;
  unit: string;
  /** The signed deviation from the measured table's 24 h cell, or null for a count. */
  lead: string | null;
  rest: string;
  tone: KpiTile['tone'];
  /** Whether the tile is a 24 h read (the downtime tile is the closed day's count). */
  day: boolean;
};

const TILE_ROW: Readonly<Record<string, string>> = {
  'ore-processed': 'ore',
  'pb-ag-concentrate': 'pb-con',
  'zn-concentrate': 'zn-con',
};

/** The band: `perfStrip`'s four tiles, each budget tile led by the signed
 * deviation of the measured table's own 24 h cell — one figure, one basis. */
export function meetingBand(state: SessionState): readonly BandCell[] {
  const cells = new Map(perfRows(state).flatMap((group) => group.rows).map((row) => [row.id, row.cells[0]!]));
  return perfStrip(state).map((tile) => {
    const match = tile.value.match(/^(.*\S)\s+(\S+)$/);
    const rowId = TILE_ROW[tile.id];
    const cell = rowId ? cells.get(rowId) : undefined;
    return {
      id: tile.id,
      label: tile.label,
      figure: match ? match[1]! : tile.value,
      unit: match ? match[2]! : '',
      lead: cell ? formatDeltaPct(cell.deltaPct) : tile.sub,
      rest: cell ? tile.sub : '',
      tone: tile.tone,
      day: Boolean(cell),
    };
  });
}

// --- The bottleneck read, split -------------------------------------------

export type BottleneckParts = { subject: string; facts: readonly string[] };

/** The plant model's bottleneck sentence (`bottleneckRead`, imported by
 * `last24hVsPlan`) drawn as its subject and its facts — split on the
 * sentence's own em dash and semicolons, never re-worded. A branch without
 * an em dash (the confirmed / provisional hardness reads) stays one fact. */
export function bottleneckParts(text: string): BottleneckParts {
  const trimmed = text.trim();
  const dash = trimmed.indexOf(' — ');
  if (dash < 0) return { subject: '', facts: [trimmed] };
  const subject = trimmed.slice(0, dash);
  const body = trimmed.slice(dash + 3).replace(/\.$/, '');
  return { subject, facts: body.split('; ').map((fact) => fact.trim()).filter(Boolean) };
}

export type MeetingBandFooter = {
  bottleneck: BottleneckParts;
  variations: readonly { id: string; label: string; threeWIds: readonly string[] }[];
};

export function meetingBandFooter(state: SessionState, advisory?: AdvisorySessionState): MeetingBandFooter {
  const plan = last24hVsPlan(state, advisory);
  return {
    bottleneck: bottleneckParts(plan.bottleneck),
    variations: plan.variations.map((variation) => ({
      id: variation.id,
      label: variation.label,
      threeWIds: variation.threeWIds,
    })),
  };
}

// --- Register meta and tab headlines ----------------------------------------

/** The register's rank counts for its panel meta (`2 critical · 3 high`),
 * read through the same `threeWPriority` the pills use. */
export function registerRankCounts(): { critical: number; high: number } {
  const open = THREE_W_ENTRIES;
  return {
    critical: open.filter((row) => threeWPriority(row) === 'critical').length,
    high: open.filter((row) => threeWPriority(row) === 'high').length,
  };
}

export type MeetingTabId = 'register' | 'perf' | 'next-24h' | 'shift-handover' | 'ccc-review';

const MEETING_TABS: readonly MeetingTabId[] = ['register', 'perf', 'next-24h', 'shift-handover', 'ccc-review'];

/** The Shift handover tab's route segment (`/meeting/shift-handover`): the Ops
 *  supervisor landing's carried-in card lands on the view that prints its count
 *  (polish.P13 adjudication 5). */
export const MEETING_SHIFT_HANDOVER_TAB: MeetingTabId = 'shift-handover';

/** Guards the `:tab` route segment against the closed tab set, as Advisory's
 *  `isAdvisoryTab` does; an unknown or missing segment is never a tab. */
export function isMeetingTab(value: string | undefined | null): value is MeetingTabId {
  return !!value && (MEETING_TABS as readonly string[]).includes(value);
}

/** Each tab's headline figure (comp `.tabs .tn`), read from the tab's own transform. */
export function meetingTabHeadlines(state: SessionState): Readonly<Record<MeetingTabId, string>> {
  const tonnes = nextDayPlanRows(state).find((row) => row.id === 'tonnes');
  return {
    register: `${openThreeWs().length} open`,
    perf: windowsFor(state).map((window) => window.label.split(' · ')[0]).join(' · '),
    'next-24h': tonnes ? tonnes.plan.split(' · ')[0]! : '',
    'shift-handover': `${handoverForShift(state).carried.length} carried`,
    'ccc-review': `${cccReview().headline.overdue} overdue`,
  };
}

/** Step 1's row count — every row due since the last meeting, across the three buckets. */
export function previousDueCount(previous: PreviousThreeWsBucket): number {
  return previous.done.length + previous.slipped.length + previous.overdue.length;
}

export type CccBandCell = { id: string; label: string; value: string; comparison: string; tone: 'ok' | 'warn' | 'bad' | 'info' };

/** The CCC review's headline band: `cccReview()`'s four counts, each with the
 * comparison the register behind it supplies. */
export function cccBand(cursor: string = MEETING_CURSOR): readonly CccBandCell[] {
  const view = cccReview(cursor);
  const overdueIds = view.cases.filter((row) => row.status === 'Overdue').map((row) => row.id);
  return [
    { id: 'open', label: 'Open CCC cases', value: String(view.headline.open), comparison: `of ${view.cases.length} on the register`, tone: 'info' },
    {
      id: 'overdue',
      label: 'Overdue on review date',
      value: String(view.headline.overdue),
      comparison: overdueIds.join(' · '),
      tone: view.headline.overdue > 0 ? 'bad' : 'ok',
    },
    {
      id: 'recurred',
      label: 'Recurred inside window',
      value: String(view.headline.recurredInWindow),
      comparison: 'Root cause likely wrong — reopened',
      tone: view.headline.recurredInWindow > 0 ? 'warn' : 'ok',
    },
    {
      id: 'held',
      label: 'Held past tracking window',
      value: String(view.headline.heldTenCycles),
      comparison: 'Root cause confirmed correct',
      tone: 'ok',
    },
  ];
}
